<template>
	<base-modal
		ref="$modal"
		:title="modalTitle"
		:loading="isLoading"
		width="640px"
		height="80vh"
		@on-close="onBaseModalClose"
	>
		<div
			ref="$content"
			class="cp-info"
		>
			<template v-if="!isLoading && info">
				<div class="cp-info__line">
					<span class="cp-info__label">{{ $t('creation_date') }}:</span>
					<span class="cp-info__value">
						{{ formatShortDate(info.Date) }}<template v-if="formatShortDate(info.Date)">,</template>
						{{ $t(info.CreatedInCrm ? 'created_in_crm' : 'created_in_one_c') }}
					</span>
				</div>
				<div
					v-if="info.Creator?.Name"
					class="cp-info__line"
				>
					<span class="cp-info__label">{{ $t('creator') }}:</span>
					<span class="cp-info__value">{{ info.Creator.Name }}</span>
				</div>

				<div
					v-for="row in requisites"
					:key="row.label"
					class="cp-info__line"
				>
					<span class="cp-info__label">{{ row.label }}:</span>
					<span class="cp-info__value">{{ row.value }}</span>
				</div>

				<div class="cp-info__line">
					<span class="cp-info__label">{{ $t('one_c_instance') }}:</span>
					<span class="cp-info__value">{{ info.Instance1C || $t('by_default') }}</span>
				</div>

				<!-- Привязан к клиенту + перепривязка -->
				<div class="cp-info__client">
					<div class="cp-info__client-head">
						<span class="cp-info__label">
							{{ $t(info.Client ? 'linked_to_client' : 'not_linked_to_client') }}:
						</span>
						<app-resizable-dropdown
							v-if="canEditClientBinding"
							ref="$reattachDropdown"
							position="right"
							resizable
							:initial-width="360"
							:initial-height="420"
							:min-width="320"
							:max-width="600"
							:min-height="300"
							:max-height="700"
							:content-z-index="10010"
							:close-by-click-outside="true"
						>
							<template #activator>
								<app-tooltip :text="$t('counterparty_change_client')">
									<template #activator>
										<app-icon
											class="cp-info__client-edit"
											name="edit-pencil"
											width="14px"
											color="#979797"
											hover-color="#499259"
											cursor="pointer"
										/>
									</template>
								</app-tooltip>
							</template>
							<client-reattach-dropdown
								:counterparty-id="currentId"
								:current-client-id="info.Client?.Id"
								show-close-icon
								@reattached="onReattached"
								@close="$reattachDropdown?.close()"
							/>
						</app-resizable-dropdown>
					</div>

					<div
						v-if="info.Client"
						class="cp-info__client-card"
					>
						<router-link
							class="cp-info__client-name"
							:to="{ query: { page: 'client', id: info.Client.Id } }"
							@click="onModalClose"
						>
							{{ info.Client.Name }}
						</router-link>
						<span
							v-if="info.Client.AddressChain?.length"
							class="cp-info__client-address"
						>({{ info.Client.AddressChain.join(', ') }})</span>
						<span
							v-if="info.Client.Tags?.length"
							class="cp-info__client-tags"
						>
							<span
								v-for="tag in info.Client.Tags"
								:key="tag.Id"
								class="cp-info__client-tag"
							>#{{ tag.Name }}</span>
						</span>
					</div>
				</div>

				<!-- Счета -->
				<div
					v-if="outgoingBills.length"
					class="cp-info__bills"
				>
					<div class="cp-info__label">{{ $t('outgoing_invoices') }}:</div>
					<div
						v-for="(bill, i) in outgoingBills"
						:key="bill.Id"
						class="cp-info__bill"
					>
						<span class="cp-info__bill-index">{{ i + 1 }}.</span>
						<span class="cp-info__bill-text">
							<app-tooltip :text="$t('bill_info_detailed')">
								<template #activator>
									<app-icon
										class="cp-info__bill-info"
										name="info-circle-outline"
										width="14px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
										@click="openBillInfo(bill)"
									/>
								</template>
							</app-tooltip>
							{{ $t('bill_out_abbr') }} <span class="cp-info__green">{{ bill.Number || '—' }}</span>
							[{{ bill.Instance1C || $t('by_default') }}]
							<span class="cp-info__green">{{ $t('date_from_prefix') }} {{ formatShortDate(bill.Date) }},</span>
							<span class="cp-info__bill-cp">{{ bill.Counterparty?.Name || $t('no_counterparty') }}</span>
							<span class="cp-info__green"> | {{ formatMoney(bill.TotalAmount ?? 0) }},</span>
							<span class="cp-info__bar">
								<span
									class="cp-info__bar-fill"
									:style="{ width: barWidth(bill.PayedPercent) }"
								/>
							</span>
							<span
								v-if="bill.HasPayments"
								class="cp-info__green"
							>{{ bill.PayedPercent ?? 0 }}% ({{ formatMoney(bill.PayedAmount ?? 0) }})</span>
							<span v-else>{{ payStatusText(bill.PayStatus) }}</span>
							<span
								v-if="bill.Note"
								class="cp-info__bill-note"
							>[{{ bill.Note }}]</span>
						</span>
					</div>
				</div>

				<div
					v-if="incomingBills.length"
					class="cp-info__bills"
				>
					<div class="cp-info__label">{{ $t('incoming_invoices') }}:</div>
					<div
						v-for="(bill, i) in incomingBills"
						:key="bill.Id"
						class="cp-info__bill"
					>
						<span class="cp-info__bill-index">{{ i + 1 }}.</span>
						<span class="cp-info__bill-text">
							<app-tooltip :text="$t('bill_info_detailed')">
								<template #activator>
									<app-icon
										class="cp-info__bill-info"
										name="info-circle-outline"
										width="14px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
										@click="openBillInfo(bill)"
									/>
								</template>
							</app-tooltip>
							{{ $t('bill_in_abbr') }} <span class="cp-info__green">{{ bill.Number || '—' }}</span>
							[{{ bill.Instance1C || $t('by_default') }}]
							<span class="cp-info__green">{{ $t('date_from_prefix') }} {{ formatShortDate(bill.Date) }},</span>
							<span class="cp-info__bill-cp">{{ bill.Counterparty?.Name || $t('no_counterparty') }}</span>
							<span class="cp-info__green"> | {{ formatMoney(bill.TotalAmount ?? 0) }},</span>
							<span class="cp-info__bar">
								<span
									class="cp-info__bar-fill"
									:style="{ width: barWidth(bill.PayedPercent) }"
								/>
							</span>
							<span
								v-if="bill.HasPayments"
								class="cp-info__green"
							>{{ bill.PayedPercent ?? 0 }}% ({{ formatMoney(bill.PayedAmount ?? 0) }})</span>
							<span v-else>{{ payStatusText(bill.PayStatus) }}</span>
							<span
								v-if="bill.Note"
								class="cp-info__bill-note"
							>[{{ bill.Note }}]</span>
						</span>
					</div>
				</div>
			</template>

			<div
				v-else-if="!isLoading && !info"
				class="cp-info__empty"
			>
				{{ $t('counterparty_info_error') }}
			</div>
		</div>

		<bill-info-modal
			ref="$billInfoModal"
			@changed="onBillChanged"
		/>
	</base-modal>
</template>

<script setup lang="ts">
import { formatMoney } from "@/utils/format";
import { computed, ref } from "vue";
import { i18n } from "@/i18n";

const t = (key: string, named?: Record<string, unknown>): string => String(named ? i18n.global.t(key, named) : i18n.global.t(key));
import BaseModal from "@/components/common/modal/BaseModal.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import ClientReattachDropdown from "@/components/main/client-block-page/dropdowns/ClientReattachDropdown.vue";
import { useQuasar } from "quasar";
import { financesStore } from "@/store/finances";
import BillInfoModal from "./BillInfoModal.vue";
import BillInfoDialog from "@/components/multiedit/temporary/storageshared/BillInfoDialog.vue";
import { CounterpartyInfo } from "@/models/interfaces/finances/counterparty-models";
import { BillWithOneLink } from "@/models/interfaces/finances/finance-models";
import { BillType, BillLinkedTypeDescriptor } from "@/models/etc/types";
import { formatShortDate, payStatusText, barWidth } from "./bill-format";
import { useModalEscape } from "@/composables/useModalEscape";

const emit = defineEmits<{
	(e: 'close'): void,
}>()

const info = ref<CounterpartyInfo | null>(null)
const isLoading = ref(true)
const currentId = ref(0)
const canEditClientBinding = ref(false)

const $content = ref<HTMLElement | null>(null)
const $modal = ref<InstanceType<typeof BaseModal> | null>(null)
const $reattachDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null)
const $billInfoModal = ref<InstanceType<typeof BillInfoModal> | null>(null)
const $q = useQuasar()

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose()
})

const modalTitle = computed(() => {
	const name = info.value?.Name || ''
	const base = t('counterparty_full_info_title', { name })
	return info.value?.IsDraft ? t('draft_suffix', { base }) : base
})

const requisites = computed(() => {
	const m = info.value
	if (!m) return []
	return [
		{ label: t('inn'), value: m.TaxpayerNumber },
		{ label: t('kpp'), value: m.ReasonCode },
		{ label: t('personal_account2'), value: m.PersonalAccount },
		{ label: t('bank_or_settlement_account'), value: m.BankAccount },
		{ label: t('bank_name'), value: m.BankName },
		{ label: t('bik'), value: m.BankIdCode },
		{ label: t('correspondent_bank_account'), value: m.CorrespondentBankAccount },
		{ label: t('legal_address_full'), value: m.LegalAddress },
		{ label: t('legal_form'), value: m.LegalForm },
		{ label: t('comment'), value: m.Note },
	].filter(row => row.value != null && String(row.value).trim() !== '')
})

const outgoingBills = computed(() =>
	(info.value?.Bills ?? []).filter(b => b.Type === BillType.Outgoing)
)
const incomingBills = computed(() =>
	(info.value?.Bills ?? []).filter(b => b.Type === BillType.Incoming)
)

const onOpen = async (counterpartyId: number, canEditBinding = false) => {
	info.value = null
	currentId.value = counterpartyId
	canEditClientBinding.value = canEditBinding
	isLoading.value = true
	$modal.value?.onOpen()
	activateEsc()
	try {
		info.value = (await financesStore().getInfoCounterparty(counterpartyId)) ?? null
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onReattached = async () => {
	info.value = (await financesStore().getInfoCounterparty(currentId.value)) ?? info.value
	$reattachDropdown.value?.close()
}

const openBillInfo = (bill: BillWithOneLink) => {
	// платёж (Descriptor===Payment) — открываем легаси-модалку, счёт — редизайн-модалку
	if (bill.Descriptor === BillLinkedTypeDescriptor.Payment) {
		$q.dialog({
			component: BillInfoDialog,
			componentProps: { existingBillId: bill.Id, isPayment: true }
		})
		return
	}
	$billInfoModal.value?.onOpen(bill.Id)
}

const onBillChanged = async () => {
	info.value = (await financesStore().getInfoCounterparty(currentId.value)) ?? info.value
}

const onModalClose = () => $modal.value?.onClose()

const onBaseModalClose = () => {
	deactivateEsc()
	emit('close')
}

defineExpose({ onOpen, onClose: onModalClose })
</script>

<style scoped lang="scss">
.cp-info {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 12px;

	&__line {
		font-size: 14px;
		line-height: 18px;
	}

	&__label {
		font-weight: 500;
		color: #4D4D4D;
	}

	&__value {
		margin-left: 6px;
		color: #333;
		word-break: break-word;
	}

	&__green {
		color: #499259;
	}

	&__client {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-top: 4px;

		&-head {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		&-edit {
			margin-top: -2px;
		}

		&-card {
			font-size: 14px;
			line-height: 18px;
		}

		&-name {
			font-weight: 500;
			color: #438451;
			text-decoration: none;

			&:hover {
				text-decoration: underline;
			}
		}

		&-address {
			margin-left: 6px;
			color: #979797;
		}

		&-tags {
			margin-left: 6px;
		}

		&-tag {
			margin-right: 6px;
			color: #979797;
		}
	}

	&__bills {
		display: flex;
		flex-direction: column;
		gap: 4px;
		margin-top: 4px;
	}

	&__label + &__bill,
	&__bills > &__label {
		font-weight: 500;
		color: #4D4D4D;
	}

	&__bill {
		display: flex;
		align-items: baseline;
		gap: 4px;
		font-size: 13px;
		line-height: 18px;
		color: #4D4D4D;

		&-index {
			flex-shrink: 0;
		}

		&-info {
			margin-top: -2px;
		}

		&-text {
			min-width: 0;
			word-break: break-word;
		}

		&-cp {
			font-weight: 600;
		}

		&-note {
			color: #979797;
		}
	}

	&__bar {
		display: inline-block;
		vertical-align: middle;
		width: 64px;
		height: 6px;
		margin: 0 4px;
		border-radius: 3px;
		background: #EDEDED;
		overflow: hidden;

		&-fill {
			display: block;
			height: 100%;
			border-radius: 3px;
			background: #499259;
			transition: width 0.2s ease;
		}
	}

	&__empty {
		font-size: 14px;
		line-height: 18px;
		color: #979797;
		padding: 12px 0;
	}
}
</style>
